import { z } from 'zod';
import { Group } from '../paths/herawApiPathGroups';
import { ApiMethod } from '../types/api.types';
import { formSchema, formWithFieldsSchema } from '../types/form.types';
import {
    formAssociationTargetSchema,
    formCreationBodySchema,
    formListQuerySchema,
    formParamsSchema,
    formUpdateBodySchema
} from '../types/payloads/form.payload';
import { workspaceParamsSchema } from '../types/payloads/workspace.payload';

export const API_FORMS = {
    LIST: {
        method: ApiMethod.GET,
        path: '/workspaces/:workspaceName/forms',
        handlerMethod: 'listForms',
        zod: {
            params: workspaceParamsSchema,
            query: formListQuerySchema
        },
        responses: {
            200: {
                description: 'The list of forms in a workspace',
                schema: z.array(formSchema)
            }
        }
    },
    LIST_OA: {
        method: ApiMethod.GET,
        path: '/public/v1/workspaces/:workspaceName/forms',
        handlerMethod: 'listForms',
        publicApi: true,
        description: 'List forms in a workspace',
        group: Group.Forms,
        zod: {
            params: workspaceParamsSchema,
            query: formListQuerySchema
        },
        responses: {
            200: {
                description: 'The list of forms in a workspace',
                schema: z.array(formSchema)
            }
        }
    },
    GET: {
        method: ApiMethod.GET,
        path: '/workspaces/:workspaceName/forms/:identifier',
        handlerMethod: 'getForm',
        zod: {
            params: formParamsSchema
        },
        responses: {
            200: {
                description: 'The form with fields',
                schema: formWithFieldsSchema
            }
        }
    },
    GET_OA: {
        method: ApiMethod.GET,
        path: '/public/v1/workspaces/:workspaceName/forms/:identifier',
        handlerMethod: 'getForm',
        publicApi: true,
        description: 'Get a form',
        group: Group.Forms,
        zod: {
            params: formParamsSchema
        },
        responses: {
            200: {
                description: 'The form with fields',
                schema: formWithFieldsSchema
            }
        }
    },
    POST_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/forms',
        handlerMethod: 'createForm',
        publicApi: true,
        description: 'Create a new form',
        group: Group.Forms,
        zod: {
            params: workspaceParamsSchema,
            body: formCreationBodySchema
        },
        responses: {
            200: {
                description: 'The created form',
                schema: formSchema
            }
        }
    },
    PATCH_OA: {
        method: ApiMethod.PATCH,
        path: '/public/v1/workspaces/:workspaceName/forms/:identifier',
        handlerMethod: 'updateForm',
        publicApi: true,
        description: 'Update a form',
        group: Group.Forms,
        zod: {
            params: formParamsSchema,
            body: formUpdateBodySchema
        },
        responses: {
            200: {
                description: 'The updated form',
                schema: formSchema
            }
        }
    },
    DELETE_OA: {
        method: ApiMethod.DELETE,
        path: '/public/v1/workspaces/:workspaceName/forms/:identifier',
        handlerMethod: 'deleteForm',
        publicApi: true,
        description: 'Delete a form',
        group: Group.Forms,
        zod: {
            params: formParamsSchema
        },
        responses: {
            204: {
                description: 'The form was successfully deleted'
            }
        }
    }
} as const;

export const API_FORM_ASSOCIATIONS = {
    POST_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/forms/:identifier/associations',
        handlerMethod: 'addFormAssociation',
        publicApi: true,
        description: 'Add a form association',
        group: Group.Forms,
        zod: {
            params: formParamsSchema,
            body: formAssociationTargetSchema
        },
        responses: {
            204: {
                description: 'The form was successfully associated'
            }
        }
    },
    DELETE_OA: {
        method: ApiMethod.DELETE,
        path: '/public/v1/workspaces/:workspaceName/forms/:identifier/associations',
        handlerMethod: 'removeFormAssociation',
        publicApi: true,
        description: 'Remove a form association',
        group: Group.Forms,
        zod: {
            params: formParamsSchema,
            query: formAssociationTargetSchema
        },
        responses: {
            204: {
                description: 'The form association was successfully removed'
            }
        }
    }
} as const;
